import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import "../styles/EditCollectionPage.css";
import { getIdentifier } from "../utils/auth";
import Tip from "../components/Tip";


/* -----------------------------
   Helpers
----------------------------- */
const isDirty = (it) =>
  (it.rotation ?? 0) !== 0 ||
  Math.abs((it.zoom ?? 1) - 1) > 1e-3 ||
  Math.abs(it.offsetX ?? 0) > 0 ||
  Math.abs(it.offsetY ?? 0) > 0;

const canvasToBlob = (canvas, type, quality) =>
  new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), type, quality)
  );
  

function EditorNote({ collectionKey }) {
  const storageKey = `tcpEditorNoteSeen:${collectionKey}`;

  const [open, setOpen] = useState(false);

  const [seen] = useState(
    localStorage.getItem(storageKey) === "1"
);
  useEffect(() => {

  if (!seen) {

    setOpen(true);

    const timer = setTimeout(() => {

      setOpen(false);

      localStorage.setItem(storageKey, "1");

    }, 3000);

    return () => clearTimeout(timer);

  }

}, [seen]);
  return (
  <>
 {open ? (

  <button
    className={`tcp-btn active`}
    onClick={() => setOpen(false)}
  >
    ⓘ
  </button>

) : (

  <Tip text="show editing tips" side="bottom">
    <button
      className="tcp-btn"
      onClick={() => setOpen(true)}
    >
      ⓘ
    </button>
  </Tip>

)}

    {open && (
  <div className="tcp-note" role="note">

    <div className="tcp-note-title">
      everything here is optional
    </div>

    <div className="tcp-note-lines">

      <span>
        💾 save any details you add.
      </span>

      <span>
        <strong>tags</strong> help you organise and filter items.
      </span>

      <span>
        🔐 locks let you keep items and details private.
      </span>

      <span>
        ➕ add more items whenever you like.
      </span>

      <span>
        ◼ view all [desktop only] lets you reorder items.
      </span>

      <span>
        🖼️ to replace the image, ⧉ add more images to your item.
      </span>

    </div>

  </div>
)}
  </>
);
}

async function loadBitmapFromItem(item) {
  if (item.file instanceof Blob || item.file instanceof File) {
    return await createImageBitmap(item.file);
  }
  const src = item.originalUrl || item.originalPreviewUrl || item.previewUrl;
  if (!src) throw new Error("No source for image");

  const resp = await fetch(src, { mode: "cors" }).catch(() => null);
  if (!resp || !resp.ok) throw new Error("Failed to fetch image for baking");
  const blob = await resp.blob();
  return await createImageBitmap(blob);
}

async function bakeEditsToBlobs(item) {
  const bmp = await loadBitmapFromItem(item);
  const natW = bmp.width;
  const natH = bmp.height;

  const LONG_EDGE_CAP = 4000;
  const scaleNat = Math.min(1, LONG_EDGE_CAP / Math.max(natW, natH));
  const outW = Math.max(1, Math.round(natW * scaleNat));
  const outH = Math.max(1, Math.round(natH * scaleNat));

  const fit = item.lastFit || 1;
  const offX_nat = (item.offsetX || 0) / fit;
  const offY_nat = (item.offsetY || 0) / fit;

  const deg = item.rotation || 0;
  const rad = (deg * Math.PI) / 180;
  const absC = Math.abs(Math.cos(rad));
  const absS = Math.abs(Math.sin(rad));
  const rotW = Math.max(1, Math.round(outW * absC + outH * absS));
  const rotH = Math.max(1, Math.round(outW * absS + outH * absC));

  const canvas = document.createElement("canvas");
  canvas.width = rotW;
  canvas.height = rotH;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, rotW, rotH);

  ctx.translate(rotW / 2, rotH / 2);
  ctx.translate(offX_nat * scaleNat, offY_nat * scaleNat);
  ctx.rotate(rad);

  const zoom = item.zoom || 1;
  ctx.scale(zoom, zoom);

  ctx.drawImage(bmp, -outW / 2, -outH / 2, outW, outH);
  bmp.close?.();

  const mime = "image/jpeg";
  const bakedBlob = await canvasToBlob(canvas, mime, 0.95);

  const maxPrev = 1600;
  const s = Math.min(1, maxPrev / Math.max(rotW, rotH));
  let previewBlob = bakedBlob;

  if (s < 1) {
    const c2 = document.createElement("canvas");
    c2.width = Math.round(rotW * s);
    c2.height = Math.round(rotH * s);
    const cx2 = c2.getContext("2d");
    cx2.drawImage(canvas, 0, 0, c2.width, c2.height);
    previewBlob = await canvasToBlob(c2, mime, 0.9);
  }

  const base = (item.file?.name || "image").replace(/\.[^.]+$/, "");
  const bakedFile = new File([bakedBlob], `${base}_edited.jpg`, { type: mime });
  const previewFile = new File([previewBlob], `${base}_preview.jpg`, { type: mime });

  return { bakedFile, previewFile };
}

/* -----------------------------
   Autosuggest input
----------------------------- */
function AutoSuggestInput({ value, onChange, suggestions = [], placeholder = "", className = "" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const tags = useMemo(
    () =>
      (value || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    [value]
  );

  useEffect(() => {
    const ix = (value || "").lastIndexOf(",");
    const token = ix === -1 ? value || "" : (value || "").slice(ix + 1);
    setQ(token.trimStart());
  }, [value]);

  const options = useMemo(() => {
    const needle = (q || "").trim().toLowerCase();
    if (!needle) return []; // ✅ don't show anything unless user typed something

    const picked = new Set(tags.map((t) => t.toLowerCase()));
    const src = suggestions.filter((t) => !picked.has(t.toLowerCase()));
    return src.filter((t) => t.toLowerCase().startsWith(needle)).slice(0, 8);
  }, [q, suggestions, tags]);


  const commit = (tag) => {
    const parts = (value || "").split(",");
    parts[parts.length - 1] = `${tag}`;
    const next = parts.join(",").replace(/^ /, "");
    onChange(next);
    setOpen(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const onKeyDown = (e) => {
    if (!open && options.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open || !options.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a - 1 + options.length) % options.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(options[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={`tcp-tags ${className}`} style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        className="tcp-tags-input"
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(false)}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        onKeyDown={onKeyDown}
        spellCheck={false}
      />

      {open && options.length > 0 && (
        <div className="tcp-tags-suggest" onMouseDown={(e) => e.preventDefault()}>
          {options.map((opt, i) => (
            <button
              key={opt}
              type="button"
              tabIndex={-1}
              className={`tcp-tags-suggest-item ${i === active ? "is-active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => commit(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -----------------------------
   Field specs
----------------------------- */
const CORE_FIELDS = [
  { key: "name", label: "title", type: "text" },
  { key: "artist", label: "creator’s name", type: "artist" },
  { key: "source", label: "acquired from", type: "source" },
  { key: "memory", label: "memory", type: "text" },
  { key: "tags", label: "tags", type: "tags" },
  { key: "sale", label: "for sale", type: "checkbox" },
];

const EXTRA_FIELDS = [
  { key: "edition", label: "edition number", type: "text" },
  { key: "location", label: "location", type: "location" },
  { key: "dimensions", label: "dimensions", type: "text" },
   { key: "material", label: "material", type: "text" },
  { key: "condition", label: "condition", type: "text" },
  { key: "price", label: "price", type: "text" },
  { key: "dateCreated", label: "date created", type: "text" },
  { key: "datePurchased", label: "date purchased", type: "text" },
  { key: "notes", label: "notes", type: "text" },
  { key: "altText", label: "alt text", type: "text" },
  { key: "attachments", label: "attachments", type: "attachments" },
  { key: "links", label: "links", type: "text" },
];

function normalizeEnabledExtras(item) {
  const enabled = new Set(item.enabledExtras || []);
  EXTRA_FIELDS.forEach((f) => {
    if (f.key === "attachments") {
      const a = item.attachments || [];
      if (Array.isArray(a) && a.length) enabled.add("attachments");
      return;
    }
    const v = (item[f.key] ?? "").toString().trim();
    if (v) enabled.add(f.key);
  });
  return Array.from(enabled);
}


/* =========================================================
   ✅ FIX: define stable components OUTSIDE the page
   (prevents re-mount each render → fixes “one letter” bug)
========================================================= */
function ConfirmBulkLock({ open, title, subtitle, onYes, onNo }) {
  if (!open) return null;

  return (
    <div className="tcp-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="tcp-modal-card">
        <div className="tcp-modal-title">{title}</div>
        {subtitle && <div className="tcp-modal-sub">{subtitle}</div>}

        <div className="tcp-modal-actions">
          <button className="tcp-btn" type="button" onClick={onYes}>
            yes
          </button>
          <button className="tcp-btn" type="button" onClick={onNo}>
            no
          </button>
        </div>
      </div>
    </div>
  );
}

const LockButton = ({ locked, onClick, label, disabled }) => (
  <button
    type="button"
    className="tcp-lock"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={disabled ? "collection is private" : locked ? "private" : "public"}
  >
    {locked ? "🔐" : "🔓"}
  </button>
);

const Editable = ({ value, onCommit, placeholder = "", multiline = false }) => (
  <span
    className={`tcp-edit ${multiline ? "is-multiline" : ""} ${value ? "" : "is-empty"}`}
    contentEditable
    suppressContentEditableWarning
    data-placeholder={placeholder}
    onInput={(e) => {
  const el = e.currentTarget;

  if (el.textContent?.trim()) {
    el.classList.remove("is-empty");
  } else {
    el.classList.add("is-empty");
  }
}}

onBlur={(e) => onCommit(e.target.textContent || "")}
  >
    {value || ""}
  </span>
);

function FieldRow({
  item,
  fieldSpec,
  lockedDisabled,
  knownArtists,
  knownSources,
  knownTags,
  knownLocations,
  updateField,
  updateItem,
  toggleFieldPrivacy,
  addAttachmentsToItem,
  removeAttachmentFromItem,
}) {
  const { key, label, type } = fieldSpec;
  const locked = key === "attachments"
  ? !!(item.privacy || {})["attachment"]
  : !!(item.privacy || {})[key];
  const showLock = key !== "tags" && key !== "altText";

  let input =
    type === "artist" ? (
      <AutoSuggestInput
        value={item.artist || ""}
        onChange={(v) => updateField(item.id, "artist", v)}
        suggestions={knownArtists}
      />
    ) : type === "source" ? (
      <AutoSuggestInput
        value={item.source || ""}
        onChange={(v) => updateField(item.id, "source", v)}
        suggestions={knownSources}
      />
    ) : type === "tags"  ? (
      <AutoSuggestInput
        value={item.tags || ""}
        onChange={(v) => updateField(item.id, "tags", v)}
        suggestions={knownTags}
        placeholder="click to add tags (e.g. big, small)"
      />
    ) : type === "location" ? (
      <AutoSuggestInput
        value={item.location || ""}
        onChange={(v) => updateField(item.id, "location", v)}
        suggestions={knownLocations}
        placeholder="city, storage, room…"
      />
    ) : type === "checkbox" ? (
  <input
    className="tcp-checkbox"
    type="checkbox"
    checked={!!item[key]}
    onChange={(e) =>
      updateField(item.id, key, e.target.checked)
    }
  />
)
 : type === "attachments" ? (
      <div className="tcp-attach">
        <label className="tcp-attach-btn">
          📎 add
          <input
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              const picked = Array.from(e.target.files || []);
              e.target.value = "";
              addAttachmentsToItem(item.id, picked);

              // auto-enable field if user adds attachments
              updateItem(item.id, {
                enabledExtras: Array.from(
                  new Set([...normalizeEnabledExtras(item), "attachments"])
                ),
              });
            }}
          />
        </label>

        <div className="tcp-attach-list">
          {(item.attachments || []).map((a, i) => {
            const url = typeof a === "string" ? a : a?.url;
            const name = typeof a === "string" ? a.split("/").pop() : a?.name || "attachment";

            return (
              <div className="tcp-attach-row" key={`${name}-${i}`}>
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    🗃️ {name}
                  </a>
                ) : (
                  <span>🗃️ {name}</span>
                )}

                <button
                  type="button"
                  className="tcp-attach-x"
                  onClick={() => removeAttachmentFromItem(item.id, i)}
                  aria-label="remove attachment"
                  title="remove"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      </div>
    ) : (
 <Editable
  value={item[key] || ""}
  onCommit={(v) => updateField(item.id, key, v)}
  multiline={key === "memory" || key === "notes" || key === "links"}
 placeholder={
  key === "memory" || key === "notes"
    ? ""
  : key === "dateCreated"
    ? "any format, e.g. 2015, May 2015, or 12 May 2015"
  : key === "datePurchased"
    ? "any format, e.g. 2018, spring 2018, or 3 June 2018"
  : key === "links"
    ? "add one link per line"
  : ""
}
/>

    );

  return (
    <div className="tcp-row">
      <div className="tcp-label">
  {label}
</div>
      <div className="tcp-value">{input}</div>
<div className="tcp-lockcell">
  {showLock ? (
    <LockButton
      locked={locked}
      disabled={lockedDisabled}
      onClick={() => toggleFieldPrivacy(item.id, key, label)}
      label={`${label} privacy`}
    />
  ) : (
    <div className="tcp-lockcell-empty" aria-hidden="true" />
  )}
</div>
    </div>
  );
}

/* -----------------------------
   Page
----------------------------- */
export default function EditCollectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const collectionNameFromState = location.state?.collectionName || null;

  const selectedItemId = location.state?.selectedItemId || null;
  const selectedSubImageIndex =
  location.state?.selectedSubImageIndex || 0;

  const requireId = () => getIdentifier() || null;

  // refs
  const fileInputRef = useRef(null);
  const additionalImageInputRef = useRef(null);
  const addMoreInputRef = useRef(null);
  const editingItemIdRef = useRef(null);
  const metaBodyRef = useRef(null);

  // state
  const [files, setFiles] = useState([]);
  const [collectionName, setCollectionName] = useState("");
  const [collectionDescription, setCollectionDescription] = useState("");
  const [collectionPrivate, setCollectionPrivate] = useState(false);
  const [globalEnabledExtras, setGlobalEnabledExtras] = useState([]); // collection-wide extras

  const [loadingCollection, setLoadingCollection] = useState(true);
  const [progress, setProgress] = useState({ active: false, percent: 0, label: "" });
  const [uploading, setUploading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [compressing, setCompressing] = useState(false);

  const [lockPrompt, setLockPrompt] = useState(null);
  const [bulkFieldDefaults, setBulkFieldDefaults] = useState({}); 


  const applyFieldPrivacyOne = (itemId, fieldKey, nextValue) => {
  setFiles((prev) =>
    prev.map((it) => {
      if (it.id !== itemId) return it;
      const privacy = { ...(it.privacy || {}) };
      const k = fieldKey === "attachments" ? "attachment" : fieldKey;
      privacy[k] = nextValue;
      return { ...it, privacy };
    })
  );
};

const [isMobile, setIsMobile] = useState(
  window.matchMedia("(max-width: 900px)").matches
);

useEffect(() => {
  const mq = window.matchMedia("(max-width: 900px)");

  const handler = (e) => setIsMobile(e.matches);

  mq.addEventListener("change", handler);

  return () => mq.removeEventListener("change", handler);
}, []);


const applyFieldPrivacyAll = (fieldKey, nextValue) => {
  setFiles((prev) =>
    prev.map((it) => {
      const privacy = { ...(it.privacy || {}) };
      const k = fieldKey === "attachments" ? "attachment" : fieldKey;
      privacy[k] = nextValue;
      return { ...it, privacy };
    })
  );
};



  // view all + zoom
  const [viewAll, setViewAll] = useState(false);
  const MIN_TILE = 200;
  const MAX_TILE = 340;
  const STEP = 20;
  const clampTile = (v) => Math.max(MIN_TILE, Math.min(MAX_TILE, v));
  const [viewAllTile, setViewAllTile] = useState(() => {
    const raw = Number(localStorage.getItem("viewAllTile"));
    return clampTile(Number.isFinite(raw) && raw ? raw : 280);
  });
  useEffect(() => {
    localStorage.setItem("viewAllTile", String(viewAllTile));
  }, [viewAllTile]);
  const atMin = viewAllTile <= MIN_TILE;
  const atMax = viewAllTile >= MAX_TILE;
  const percent = ((viewAllTile - MIN_TILE) / (MAX_TILE - MIN_TILE)) * 100;

  const currentItem = files[currentIndex] || null;

  const knownTags = useMemo(() => {
    const set = new Set();
    files.forEach((it) => {
      (it.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .forEach((t) => set.add(t));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [files]);

  const knownArtists = useMemo(() => {
    const set = new Set();
    files.forEach((it) => {
      const v = (it.artist || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [files]);

  const knownSources = useMemo(() => {
    const set = new Set();
    files.forEach((it) => {
      const v = (it.source || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [files]);

  const knownLocations = useMemo(() => {
    const set = new Set();
    files.forEach((it) => {
      const v = (it.location || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [files]);

  const updateItem = (id, patch) => {
    setFiles((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const updateField = (id, field, value) => updateItem(id, { [field]: value });

const toggleFieldPrivacy = (id, fieldKey, labelForUI = "") => {
  if (fieldKey === "tags" || fieldKey === "altText") return;

  const item = files.find((x) => x.id === id);
  if (!item) return;

  const current = !!(item.privacy || {})[fieldKey];
  const nextValue = !current;

  // ✅ only auto-apply for THIS field (if user previously said YES for this field)
  const remembered = !!(item.bulkLockAllFields || {})[fieldKey];
  if (remembered) {
    applyFieldPrivacyAll(fieldKey, nextValue);
    return;
  }

  // ✅ otherwise ask (NO is not remembered, so it will ask again next time)
  setLockPrompt({
    itemId: id,
    fieldKey,
    nextValue,
    label: labelForUI || fieldKey,
  });
};

  // item-level privacy lock (persisted via `public`)
  const toggleItemPrivate = (id) => {
    setFiles((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;

        const next = !it.itemPrivate;

        const privacy = { ...(it.privacy || {}) };
        const ALL_FIELDS = [
          "name",
          "artist",
          "dateCreated",
          "source",
          "edition",
          "memory",
          "tags",
          "location",
          "condition",
          "material",
          "dimensions",
          "price",
          "datePurchased",
          "notes",
          "altText",
          "attachment",
        ];

        ALL_FIELDS.forEach((k) => {
          privacy[k] = next ? true : false;
        });

        return { ...it, itemPrivate: next, privacy };
      })
    );
  };

  const toggleExtraFieldsPanel = (id) => {
    setFiles((prev) =>
      prev.map((it) => (it.id === id ? { ...it, extraFieldsOpen: !it.extraFieldsOpen } : it))
    );
  };

const toggleExtraFieldEnabled = (id, fieldKey) => {
  const clicked = files.find((it) => it.id === id);
  const clickedEnabled = clicked
    ? new Set(normalizeEnabledExtras(clicked))
    : new Set();

  const turningOn = !clickedEnabled.has(fieldKey);

  setFiles((prev) => {
    const curGlobal = new Set(globalEnabledExtras);

    if (turningOn) curGlobal.add(fieldKey);
    else curGlobal.delete(fieldKey);

    const nextGlobalArr = Array.from(curGlobal);

    const nextItems = prev.map((it) => {
      const cur = new Set(normalizeEnabledExtras(it));

      if (turningOn) {
        cur.add(fieldKey);
      } else {
        const isAttachments = fieldKey === "attachments";

        const hasData = isAttachments
          ? Array.isArray(it.attachments) && it.attachments.length > 0
          : ((it[fieldKey] ?? "").toString().trim().length > 0);

        if (!hasData) cur.delete(fieldKey);
      }

      return {
        ...it,
        enabledExtras: Array.from(cur)
      };
    });

    setGlobalEnabledExtras(nextGlobalArr);

    return nextItems;
  });

  if (turningOn) {
   setTimeout(() => {
  metaBodyRef.current?.scrollBy({
    top: 160,
    behavior: "smooth"
  });
}, 100);
  }
};

  /* -----------------------------
     Attachments
  ----------------------------- */
  const addAttachmentsToItem = (itemId, pickedFiles) => {
    const filesArr = Array.from(pickedFiles || []);
    if (!filesArr.length) return;

    setFiles((prev) =>
      prev.map((it) =>
        it.id === itemId
          ? { ...it, attachments: [...(it.attachments || []), ...filesArr] }
          : it
      )
    );
  };

  const removeAttachmentFromItem = (itemId, idx) => {
    setFiles((prev) =>
      prev.map((it) =>
        it.id === itemId
          ? { ...it, attachments: (it.attachments || []).filter((_, i) => i !== idx) }
          : it
      )
    );
  };

  /* -----------------------------
     Delete item
  ----------------------------- */
  const deleteItem = (id) => {
    setFiles((prev) => {
      const idx = prev.findIndex((x) => x.id === id);
      const next = prev.filter((x) => x.id !== id);
      const nextIndex = Math.max(0, Math.min(next.length - 1, idx));
      setCurrentIndex(nextIndex);
      return next;
    });
  };

  /* -----------------------------
     Delete current image inside an item
  ----------------------------- */
  const deleteCurrentImage = (itemId) => {
    setFiles((prev) =>
      prev
        .map((it) => {
          if (it.id !== itemId) return it;

          const allImages = [
            {
              previewUrl: it.originalPreviewUrl || it.originalUrl || it.previewUrl,
              originalUrl: it.originalUrl,
              zoom: it.zoom,
              rotation: it.rotation,
              offsetX: it.offsetX,
              offsetY: it.offsetY,
              file: it.file,
            },
            ...(it.additionalImages || []),
          ];

          const currentImgIndex = it.currentImageIndex || 0;

          const updated = [...allImages];
          updated.splice(currentImgIndex, 1);

          if (updated.length === 0) return null;

          const nextIndex = Math.min(Math.max(0, currentImgIndex - 1), updated.length - 1);
          const [newMain, ...additional] = updated;

          return {
            ...it,
            file: newMain.file ?? it.file,
            previewUrl: newMain.previewUrl ?? it.previewUrl,
            originalPreviewUrl: newMain.previewUrl ?? it.originalPreviewUrl ?? it.originalUrl,
            originalUrl: newMain.originalUrl ?? it.originalUrl,

            rotation: newMain.rotation ?? 0,
            zoom: newMain.zoom ?? 1,
            offsetX: newMain.offsetX ?? 0,
            offsetY: newMain.offsetY ?? 0,

            additionalImages: additional,
            currentImageIndex: nextIndex,
          };
        })
        .filter(Boolean)
    );

    setCurrentIndex((i) => Math.max(0, Math.min(i, files.length - 2)));
  };

  /* -----------------------------
     Load collection
  ----------------------------- */
  useEffect(() => {
    const run = async () => {
      if (!collectionNameFromState) {
        setLoadingCollection(false);
        return;
      }
      const identifier = requireId();
      if (!identifier) {
        setLoadingCollection(false);
        return;
      }

      setLoadingCollection(true);
      setProgress({ active: true, percent: 1, label: "loading collection…" });

      try {
        const resp = await fetch("https://squid-app-6s2hc.ondigitalocean.app/get-collection", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: identifier, collectionName: collectionNameFromState }),
        });

        const data = await resp.json();
        if (resp.ok && data.collection) {
          const { name, description, items } = data.collection;
          setCollectionName(name || collectionNameFromState);
          setCollectionDescription(description || "");
          setCollectionPrivate(!!data.collection.collectionPrivate);

          const enriched = (items || []).map((item) => {
            const id = item.id || crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 10);
            const privacy = item.privacy || {};

            const base = {
              ...item,
              id,

              rotation: item.rotation || 0,
              zoom: item.zoom || 1,
              offsetX: item.offsetX || 0,
              offsetY: item.offsetY || 0,

              type: "image",
              file: null,

              previewUrl: item.previewUrl || item.fileUrl,
              originalPreviewUrl: item.previewUrl || item.fileUrl,
              originalUrl: item.originalUrl,

              additionalImages: item.additionalImages || [],
              currentImageIndex: item.currentImageIndex || 0,

              name: item.name || "",
              artist: item.artist || "",
              dateCreated: item.dateCreated || "",
              datePurchased: item.datePurchased || "",
              memory: item.memory || "",
              material: item.material || "",
              dimensions: item.dimensions || "",
              edition: item.edition || "",
              location: item.location || "",
              condition: item.condition || "",
              price: item.price || "",
              tags: item.tags || "",
              source: item.source || "",
              altText: item.altText || "",
              notes: item.notes || "",
              sale: !!item.sale,
              links: item.links || "",

              attachments: Array.isArray(item.attachments) ? item.attachments : [],

              // backend uses `public`
              itemPrivate: item.public === false,

              privacy: {
                name: !!privacy.name,
                artist: !!privacy.artist,
                dateCreated: !!privacy.dateCreated,
                source: !!privacy.source,
                edition: !!privacy.edition,
                material: !!privacy.material,
                dimensions: !!privacy.dimensions,
                memory: !!privacy.memory,
                tags: false,
                location: !!privacy.location,
                condition: !!privacy.condition,
                price: !!privacy.price,
                datePurchased: !!privacy.datePurchased,
                notes: !!privacy.notes,
                altText: item.public === false,
                attachment: !!(privacy.attachment ?? privacy.attachments),
              },

              extraFieldsOpen: false,
              enabledExtras: globalEnabledExtras,
              bulkLockAllEnabled: {},
            };

            base.enabledExtras = normalizeEnabledExtras(base);
            return base;
          });

         setFiles(enriched);

      if (selectedItemId) {
        const index = enriched.findIndex(
          (item) => item.id === selectedItemId
        );

        if (index >= 0) {
          setCurrentIndex(index);

          enriched[index].currentImageIndex =
            selectedSubImageIndex;
        } else {
          setCurrentIndex(0);
        }
      } else {
        setCurrentIndex(0);
      }

          
        }
      } finally {
        setLoadingCollection(false);
        setProgress((p) => ({ ...p, active: false }));
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionNameFromState]);

  /* -----------------------------
     Image toolbar ops (main image only)
  ----------------------------- */
  const rotateImage = (id) => {
    setFiles((prev) => prev.map((it) => (it.id === id ? { ...it, rotation: (it.rotation || 0) + 90 } : it)));
  };
  const zoomIn = (id) => {
    setFiles((prev) => prev.map((it) => (it.id === id ? { ...it, zoom: Math.min((it.zoom || 1) + 0.1, 3) } : it)));
  };
  const zoomOut = (id) => {
    setFiles((prev) => prev.map((it) => (it.id === id ? { ...it, zoom: Math.max((it.zoom || 1) - 0.1, 0.5) } : it)));
  };
  const moveImage = (id, dx, dy) => {
    setFiles((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, offsetX: (it.offsetX || 0) + dx, offsetY: (it.offsetY || 0) + dy } : it
      )
    );
  };

  /* -----------------------------
     View-all drag reorder
  ----------------------------- */
  const draggingIdRef = useRef(null);
  const dragOverIndexRef = useRef(null);

  const reorder = (arr, from, to) => {
    const copy = [...arr];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };

  const onThumbDragStart = (e, idx) => {
    const id = files[idx]?.id;
    if (!id) return;
    draggingIdRef.current = id;
    dragOverIndexRef.current = idx;

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };

  const onThumbDragOver = (e, idx) => {
    e.preventDefault();
    dragOverIndexRef.current = idx;
    e.currentTarget.classList.add("is-dragover");
  };

  const onThumbDragLeave = (e) => {
    e.currentTarget.classList.remove("is-dragover");
  };

  const onThumbDrop = (e, idx) => {
    e.preventDefault();
    e.currentTarget.classList.remove("is-dragover");

    const draggedId = draggingIdRef.current;
    draggingIdRef.current = null;
    if (!draggedId) return;

    setFiles((prev) => {
      const from = prev.findIndex((it) => it.id === draggedId);
      const to = idx;
      if (from === -1 || to === -1 || from === to) return prev;

      const next = reorder(prev, from, to);

      setCurrentIndex((cur) => {
        const curId = prev[cur]?.id;
        if (!curId) return 0;
        const newIx = next.findIndex((it) => it.id === curId);
        return Math.max(0, newIx);
      });

      return next;
    });
  };

  const onThumbDragEnd = () => {
    document.querySelectorAll(".tcp-thumb.is-dragover").forEach((el) => el.classList.remove("is-dragover"));
    draggingIdRef.current = null;
    dragOverIndexRef.current = null;
  };

  /* -----------------------------
     Add / Replace images
  ----------------------------- */
  const generateId = () => crypto?.randomUUID?.() || Math.random().toString(36).substring(2, 10);

  const addMoreItems = async (pickedFiles) => {
    const accepted = Array.isArray(pickedFiles) ? pickedFiles : Array.from(pickedFiles || []);
    if (!accepted.length) return;

    setCompressing(true);
    setProgress({ active: true, percent: 1, label: "adding items…" });

    try {
      const enriched = await Promise.all(
        accepted.map(async (file) => {
          const lower = (file?.name || "").toLowerCase();
          const isTiff = file.type === "image/tiff" || lower.endsWith(".tif") || lower.endsWith(".tiff");
          const looksImageByExt = /\.(jpg|jpeg|png|webp|gif|bmp|heic|heif|avif|tif|tiff)$/i.test(lower);
          const isImage = (file.type || "").startsWith("image/") || isTiff || looksImageByExt;
          if (!isImage) return null;

          const previewFile = await imageCompression(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 800,
            useWebWorker: true,
          });

          return {
            id: generateId(),
            file,
            previewFile,
            previewUrl: URL.createObjectURL(previewFile),
            originalPreviewUrl: URL.createObjectURL(file),

            rotation: 0,
            zoom: 1,
            offsetX: 0,
            offsetY: 0,

            type: "image",
            additionalImages: [],
            currentImageIndex: 0,

            name: "",
            artist: "",
            dateCreated: "",
            source: "",
            edition: "",
            memory: "",
            material: "",
            dimensions: "",
            tags: "",
            location: "",
            condition: "",
            price: "",
            datePurchased: "",
            notes: "",
            altText: "",
            sale: false,
            attachments: [],
            itemPrivate: false,

            privacy: {
              attachments: false,
              ...(bulkFieldDefaults || {}),
              tags: false,           // keep your rule
              altText: false,        // or whatever you want by default
            },
            extraFieldsOpen: false,
            enabledExtras: [...globalEnabledExtras], // ✅ pre-select extras on new items
            bulkLockAllEnabled: {}
          };
        })
      );

      const valid = enriched.filter(Boolean);
      if (!valid.length) return;

      setFiles((prev) => {
        const startIndex = prev.length;
        const next = [...prev, ...valid];

        setTimeout(() => {
          setViewAll(false);
          setCurrentIndex(startIndex);
        }, 0);

        return next;
      });
    } finally {
      setProgress((p) => ({ ...p, active: false }));
      setCompressing(false);
    }
  };

  const onAddMorePicked = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    await addMoreItems(picked);
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    const itemId = editingItemIdRef.current;
    e.target.value = "";
    if (!file || !itemId) return;

    setCompressing(true);
    try {
      const processed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });
      const previewUrl = URL.createObjectURL(processed);
      const originalPreviewUrl = URL.createObjectURL(file);

      setFiles((prev) =>
        prev.map((it) =>
          it.id === itemId
            ? {
                ...it,
                file,
                previewFile: processed,
                previewUrl,
                originalPreviewUrl,
                rotation: 0,
                zoom: 1,
                offsetX: 0,
                offsetY: 0,
                currentImageIndex: 0,
              }
            : it
        )
      );
    } finally {
      setCompressing(false);
    }
  };

  const ensureNamedFile = (blobOrFile, fallbackName = "additional.jpg") => {
    if (!blobOrFile) return null;
    if (blobOrFile instanceof File && blobOrFile.name) return blobOrFile;
    const type = blobOrFile.type || "image/jpeg";
    const ext = type.includes("png") ? "png" : type.includes("webp") ? "webp" : type.includes("gif") ? "gif" : "jpg";
    const safe = fallbackName.replace(/\.[^.]+$/, "");
    return new File([blobOrFile], `${safe}.${ext}`, { type });
  };

  const handleAddAdditionalImages = async (e) => {
    const chosen = Array.from(e.target.files || []);
    const itemId = editingItemIdRef.current;
    e.target.value = "";
    if (!chosen.length || !itemId) return;

    setCompressing(true);
    setProgress({ active: true, percent: 1, label: "adding images…" });

    try {
      const newImages = await Promise.all(
        chosen.map(async (file) => {
          try {
            const processed = await imageCompression(file, {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            });

            const named = ensureNamedFile(
              processed,
              (file.name || "additional").replace(/\.[^.]+$/, "") + "_additional"
            );

            return {
              file: named,
              previewUrl: URL.createObjectURL(named),
              rotation: 0,
              zoom: 1,
              offsetX: 0,
              offsetY: 0,
            };
          } catch {
            return null;
          }
        })
      );

      setFiles((prev) =>
        prev.map((it) =>
          it.id === itemId
            ? { ...it, additionalImages: [...(it.additionalImages || []), ...newImages.filter(Boolean)] }
            : it
        )
      );
    } finally {
      setCompressing(false);
      setProgress({ active: false, percent: 0, label: "" });
    }
  };

  /* -----------------------------
     Save pipeline
  ----------------------------- */
  const computeSavePct = (phase, frac) => {
    const SAVE_WEIGHTS = { presign: 0.2, upload: 0.7, metadata: 0.1 };
    const { presign, upload, metadata } = SAVE_WEIGHTS;
    const base = phase === "presign" ? 0 : phase === "upload" ? presign : presign + upload;
    const span = phase === "presign" ? presign : phase === "upload" ? upload : metadata;
    return Math.round((base + span * Math.max(0, Math.min(1, frac))) * 100);
  };

  const fetchPresignedUrls = async (onTick, identifier, items = files) => {
    const emailId = identifier ?? requireId();
    if (!emailId) throw new Error("Missing user identifier for presign.");

    let presignTotal = 0;
    for (const it of items) {
      if (!it) continue;
      if (it.file && it.previewFile) presignTotal += 2;
      for (const img of it.additionalImages || []) {
        if (img?.file && !img.uploadUrl) presignTotal += 1;
      }
      const raw = it.attachments || [];
      const newFiles = raw.filter((a) => a instanceof File);
      presignTotal += newFiles.length;
    }

    let presignDone = 0;
    const tick = () => onTick?.(++presignDone, Math.max(1, presignTotal));
    if (presignTotal === 0) onTick?.(1, 1);

    const results = await Promise.all(
      items.map(async (item) => {
        if (!item) return null;

        const uploads = [];
        let originalUrl = item.originalUrl ?? null;
        let fileUrl = item.fileUrl ?? item.previewUrl ?? null;

        if (item.file && item.previewFile) {
          const resOriginal = await fetch("https://squid-app-6s2hc.ondigitalocean.app/get-presigned-url", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: emailId,
              collectionName: collectionName.trim(),
              filename: item.file.name,
              contentType: item.file.type,
              overwriteOf: item.originalUrl || item.fileUrl,
            }),
          });
          const dataOriginal = await resOriginal.json();
          tick();
          uploads.push({ type: "original", uploadUrl: dataOriginal.url, fileUrl: dataOriginal.fileUrl, file: item.file });

          const resPreview = await fetch("https://squid-app-6s2hc.ondigitalocean.app/get-presigned-url", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: emailId,
              collectionName: collectionName.trim(),
              filename: `preview_${item.file.name}`,
              contentType: item.previewFile.type,
              overwriteOf: item.fileUrl,
            }),
          });
          const dataPreview = await resPreview.json();
          tick();
          uploads.push({ type: "preview", uploadUrl: dataPreview.url, fileUrl: dataPreview.fileUrl, file: item.previewFile });

          originalUrl = dataOriginal.fileUrl;
          fileUrl = dataPreview.fileUrl;
        } else {
          originalUrl = item.originalUrl ?? originalUrl;
          fileUrl = item.fileUrl ?? fileUrl;
        }

        const additionalImages = await Promise.all(
          (item.additionalImages || []).map(async (img) => {
            if (!img?.file || img.uploadUrl) return img;
            const res = await fetch("https://squid-app-6s2hc.ondigitalocean.app/get-presigned-url", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                filename: img.file.name,
                email: emailId,
                collectionName: collectionName.trim(),
              }),
            });
            const data = await res.json();
            tick();
            return { ...img, uploadUrl: data.url, fileUrl: data.fileUrl };
          })
        );

        const raw = item.attachments || [];
        const existingUrls = raw.map((a) => (typeof a === "string" ? a : a?.url)).filter(Boolean);
        const newFiles = raw.filter((a) => a instanceof File);

        const uploadedUrls = await Promise.all(
          newFiles.map(async (file) => {
            const res = await fetch("https://squid-app-6s2hc.ondigitalocean.app/get-presigned-url", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                filename: file.name,
                email: emailId,
                collectionName: collectionName.trim(),
              }),
            });
            const data = await res.json();
            tick();

            uploads.push({ type: "attachment", uploadUrl: data.url, fileUrl: data.fileUrl, file });
            return data.fileUrl;
          })
        );

        const attachments = [...existingUrls, ...uploadedUrls];

        return { ...item, uploads, additionalImages, attachments, fileUrl, originalUrl };
      })
    );

    return results.filter(Boolean);
  };

  const uploadAllFiles = async (itemsWithUrls, onTick) => {
    const MAX = 5;
    const all = [];

    for (const item of itemsWithUrls) {
      (item.uploads || []).forEach((u) => all.push({ itemId: item.id, ...u }));
      (item.additionalImages || []).forEach((img) => {
        if (img.file && img.uploadUrl) {
          all.push({ itemId: item.id, file: img.file, uploadUrl: img.uploadUrl, fileUrl: img.fileUrl });
        }
      });
    }

    let done = 0;
    const total = all.length;
    if (total === 0) {
      onTick?.(1, 1);
      return itemsWithUrls;
    }

    const uploadSingle = async (u) => {
      await fetch(u.uploadUrl, { method: "PUT", headers: { "Content-Type": u.file.type }, body: u.file });
      onTick?.(++done, total);
    };

    for (let i = 0; i < all.length; i += MAX) {
      await Promise.all(all.slice(i, i + MAX).map(uploadSingle));
    }

    return itemsWithUrls;
  };

  const buildChunkPayload = (chunk) =>
    chunk.map((item) => {
      const baked = !!item.__baked;
      const dirty = isDirty(item);

      return {
        id: item.id,
        fileUrl: item.fileUrl,
        originalUrl: item.originalUrl,
        type: item.type,

        public: item.itemPrivate ? false : true,

        rotation: baked ? 0 : dirty ? item.rotation || 0 : 0,
        zoom: baked ? 1 : dirty ? item.zoom || 1 : 1,
        offsetX: baked ? 0 : dirty ? item.offsetX || 0 : 0,
        offsetY: baked ? 0 : dirty ? item.offsetY || 0 : 0,

        additionalImages: (item.additionalImages || []).map((img) => ({
          fileUrl: img.fileUrl,
          previewUrl: img.previewUrl,
          rotation: img.rotation || 0,
          zoom: img.zoom || 1,
          offsetX: img.offsetX || 0,
          offsetY: img.offsetY || 0,
        })),

        currentImageIndex: item.currentImageIndex || 0,

        name: item.name || "",
        artist: item.artist || "",
        dateCreated: item.dateCreated || "",
        datePurchased: item.datePurchased || "",
        memory: item.memory || "",
        edition: item.edition || "",
        material: item.material || "",
        dimensions: item.dimensions || "",
        location: item.location || "",
        condition: item.condition || "",
        price: item.price || "",
        tags: item.tags || "",
        source: item.source || "",
        altText: item.altText || "",
        notes: item.notes || "",
        links: item.links || "",
        sale: !!item.sale,
        attachments: Array.isArray(item.attachments)
          ? item.attachments.map((a) => (typeof a === "string" ? a : a?.url)).filter(Boolean)
          : [],

        privacy: {
  ...(item.privacy || {}),
   attachment: !!((item.privacy || {}).attachment ?? (item.privacy || {}).attachments),
  tags: false,                 // ✅ always no per-field privacy
  altText: !!item.itemPrivate, // ✅ matches item visibility
},

      };
    });

  const saveMetadataChunk = async (chunk, isFirstChunk, originalNameOverride, identifier) => {
    const payload = {
      email: identifier,
      collectionName: collectionName.trim(),
      originalName: originalNameOverride,
      collectionDescription,
      collectionPrivate,
      isEditMode: true,
      isFirstChunk,
      items: buildChunkPayload(chunk),
    };

    const resp = await fetch("https://squid-app-6s2hc.ondigitalocean.app/save-collection-chunk", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) throw new Error("Failed to save metadata chunk");
  };

  const handleSubmit = async () => {
    setSaveError(null);
    setUploading(true);
    setProgress({ active: true, percent: 1, label: "preparing…" });

    const identifier = requireId();
    if (!identifier) {
      setUploading(false);
      return;
    }

    try {
      setProgress({ active: true, percent: 5, label: "rendering edits…" });

      const itemsForSave = await Promise.all(
        files.map(async (it) => {
          if (!isDirty(it)) return it;
          const { bakedFile, previewFile } = await bakeEditsToBlobs(it);
          return {
            ...it,
            file: bakedFile,
            previewFile,
            originalPreviewUrl: URL.createObjectURL(previewFile),
            __baked: true,
            rotation: 0,
            zoom: 1,
            offsetX: 0,
            offsetY: 0,
            lastFit: 1,
          };
        })
      );

      const itemsWithUrls = await fetchPresignedUrls(
        (done, total) => {
          setProgress({
            active: true,
            label: "requesting storage…",
            percent: computeSavePct("presign", done / Math.max(1, total)),
          });
        },
        identifier,
        itemsForSave
      );

      await uploadAllFiles(itemsWithUrls, (done, total) => {
        setProgress({
          active: true,
          label: "uploading images…",
          percent: computeSavePct("upload", done / Math.max(1, total)),
        });
      });

      const CHUNK_SIZE = 20;

      if (itemsWithUrls.length === 0) {
        await saveMetadataChunk([], true, collectionNameFromState, identifier);

        setProgress({ active: true, label: "finalizing…", percent: 100 });
        navigate("/post-save", { state: { collectionName } });
        return;
      }

      const numChunks = Math.ceil(itemsWithUrls.length / CHUNK_SIZE);
      let chunkDone = 0;

      for (let i = 0; i < itemsWithUrls.length; i += CHUNK_SIZE) {
        const chunk = itemsWithUrls.slice(i, i + CHUNK_SIZE);
        const isFirst = i === 0;
        const originalForThisChunk = isFirst ? collectionNameFromState : collectionName.trim();

        await saveMetadataChunk(chunk, isFirst, originalForThisChunk, identifier);
        chunkDone += 1;

        setProgress({
          active: true,
          label: "finalizing…",
          percent: computeSavePct("metadata", chunkDone / numChunks),
        });
      }

      navigate("/post-save", { state: { collectionName } });
    } catch (err) {
      console.error(err);
      setSaveError("collection not saved :( please try again or contact the founder: shaq@thecollectorsplatform.com");
    } finally {
      setUploading(false);
      setProgress((p) => ({ ...p, active: false }));
    }
  };

  /* -----------------------------
     Pager + keyboard arrows
  ----------------------------- */
  const goPrev = () => setCurrentIndex((i) => (i === 0 ? files.length - 1 : i - 1));
  const goNext = () => setCurrentIndex((i) => (i === files.length - 1 ? 0 : i + 1));

  useEffect(() => {
    const onKeyDown = (e) => {
      const isFormField =
        /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable;
      if (isFormField) return;

      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key.toLowerCase() === "g") setViewAll(true);
      if (e.key === "Escape") setViewAll(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  /* -----------------------------
     Render main image (with variants)
  ----------------------------- */
  const renderMainImage = (item) => {
    const allImages = [
      {
        previewUrl: item.originalPreviewUrl || item.originalUrl || item.previewUrl,
        originalUrl: item.originalUrl,
        zoom: item.zoom,
        rotation: item.rotation,
      },
      ...(item.additionalImages || []),
    ];

    const currentImgIndex = item.currentImageIndex || 0;
    const current = allImages[currentImgIndex];

    const src =
      currentImgIndex === 0
        ? item.originalPreviewUrl || item.originalUrl || current.previewUrl
        : current.previewUrl;

    return (
      <div className="tcp-stage">
        <div className="tcp-stage-toolbar" aria-label="image tools">
          {!isMobile && (
    <>
          <Tip text="rotate 90°">
  <button className="tcp-tool" type="button" onClick={() => rotateImage(item.id)} disabled={currentImgIndex !== 0}>
    🔁
  </button>
</Tip>

<Tip text="zoom in">
  <button className="tcp-tool" type="button" onClick={() => zoomIn(item.id)} disabled={currentImgIndex !== 0}>
    🔎
  </button>
</Tip>

<Tip text="zoom out">
  <button className="tcp-tool" type="button" onClick={() => zoomOut(item.id)} disabled={currentImgIndex !== 0}>
    🔍
  </button>
</Tip>

<Tip text="move up">
  <button className="tcp-tool" type="button" onClick={() => moveImage(item.id, 0, -10)} disabled={currentImgIndex !== 0}>
    ⬆
  </button>
</Tip>

<Tip text="move left">
  <button className="tcp-tool" type="button" onClick={() => moveImage(item.id, -10, 0)} disabled={currentImgIndex !== 0}>
    ⬅
  </button>
</Tip>

<Tip text="move right">
  <button className="tcp-tool" type="button" onClick={() => moveImage(item.id, 10, 0)} disabled={currentImgIndex !== 0}>
    ⮕
  </button>
</Tip>

<Tip text="move down">
  <button className="tcp-tool" type="button" onClick={() => moveImage(item.id, 0, 10)} disabled={currentImgIndex !== 0}>
    ⬇
  </button>
</Tip>
<Tip text="replace main image">
  <button
    className="tcp-tool"
    type="button"
    onClick={() => {
      editingItemIdRef.current = item.id;
      fileInputRef.current?.click();
    }}
    disabled={currentImgIndex !== 0}
  >
    🖼️
  </button>
</Tip>

<Tip text="add more images to this item">
  <button
    className="tcp-tool"
    type="button"
    onClick={() => {
      editingItemIdRef.current = item.id;
      additionalImageInputRef.current?.click();
    }}
  >
    ⧉
  </button>
</Tip>

<Tip text="delete this item">
  <button className="tcp-tool" type="button" onClick={() => deleteItem(item.id)}>
    🗑️
  </button>
</Tip>
</>
)}

<div className="tcp-tool-divider" />
{isMobile && (
    <>
<Tip text="replace main image">
  <button
    className="tcp-tool"
    type="button"
    onClick={() => {
      editingItemIdRef.current = item.id;
      fileInputRef.current?.click();
    }}
    disabled={currentImgIndex !== 0}
  >
    🖼️ 
  </button>
</Tip>

<Tip text="add more images to this item">
  <button
    className="tcp-tool"
    type="button"
    onClick={() => {
      editingItemIdRef.current = item.id;
      additionalImageInputRef.current?.click();
    }}
  >
    ⧉
  </button>
</Tip>

<Tip text="delete this item">
  <button className="tcp-tool" type="button" onClick={() => deleteItem(item.id)}>
    🗑️
  </button>
</Tip>
</>
 )}
        </div>

        <div className="tcp-imageframe">
          <img
            className="tcp-img"
            src={src}
            alt={item.altText || "collection item"}
            style={{
              transform: `translate(${item.offsetX || 0}px, ${item.offsetY || 0}px) rotate(${current.rotation}deg) scale(${current.zoom})`,
              transformOrigin: "center center",
              willChange: "transform",
            }}
          />

          {allImages.length > 1 && (
            <>
              <div className="tcp-dots" aria-label="image variants">
                {allImages.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`tcp-dot ${i === currentImgIndex ? "is-on" : ""}`}
                    onClick={() => updateItem(item.id, { currentImageIndex: i })}
                    aria-label={`image ${i + 1}`}
                  />
                ))}
              </div>

              <button
                type="button"
                className="tcp-img-delete"
                onClick={() => deleteCurrentImage(item.id)}
                aria-label="remove this image"
                title="remove this image"
              >
                ×
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  /* -----------------------------
     Guards
  ----------------------------- */
  if (loadingCollection) {
    return (
      <div className="tcp-edit-page">
        <div className="tcp-loading">
          {progress.active ? `${progress.label} ${progress.percent}%` : "loading your collection…"}
        </div>
      </div>
    );
  }

  if (!collectionNameFromState) {
    return (
      <div className="tcp-edit-page">
        <div className="tcp-empty">
          <p>We couldn’t find that collection.</p>
          <button className="tcp-btn" onClick={() => navigate("/home-page")}>
            go home
          </button>
        </div>
      </div>
    );
  }

  /* -----------------------------
     Main render
  ----------------------------- */
  return (
    <div className="tcp-edit-page">
      {/* HEADER */}
      <header className="tcp-header">
        <div className="tcp-header-left">
          <input
            className="tcp-collection-name"
            value={collectionName}
            onChange={(e) => setCollectionName(e.target.value)}
            placeholder="untitled collection"
            spellCheck={false}
          />
          <span className="tcp-count" title="items">
            {files.length} items
          </span>

          <button
            type="button"
            className="tcp-iconbtn"
            onClick={() => setCollectionPrivate((p) => !p)}
            title={collectionPrivate ? "collection is private" : "collection is public"}
            aria-label="toggle collection privacy"
          >
            {collectionPrivate ? "🔐" : "🔓"}
          </button>
          
        </div>

        <div className="tcp-header-right">
          {!isMobile && viewAll && (
            <div className="tcp-zoom" role="group" aria-label="thumbnail size">
              <button
                type="button"
                className="tcp-zoom-btn"
                onClick={() => setViewAllTile((v) => clampTile(v - STEP))}
                disabled={atMin}
                aria-label="smaller thumbnails"
                title="smaller"
              >
                −
              </button>

              <input
                className="tcp-zoom-slider"
                type="range"
                min={MIN_TILE}
                max={MAX_TILE}
                step={STEP}
                value={viewAllTile}
                onChange={(e) => setViewAllTile(clampTile(Number(e.target.value)))}
                style={{ "--fill": `${percent}%` }}
                aria-label="thumbnail size slider"
              />

              <button
                type="button"
                className="tcp-zoom-btn"
                onClick={() => setViewAllTile((v) => clampTile(v + STEP))}
                disabled={atMax}
                aria-label="larger thumbnails"
                title="larger"
              >
                +
              </button>
            </div>
          )}

          <Tip text="save changes" side="bottom">
              <button type="button" className="tcp-btn" onClick={handleSubmit} disabled={uploading || !collectionName.trim()}>
                💾
              </button>
            </Tip>
          
          <Tip text="add new items" side="bottom">
            <button type="button" className="tcp-btn" onClick={() => addMoreInputRef.current?.click()} disabled={uploading}>
              ➕
            </button>
          </Tip>

 {!isMobile && (
  viewAll ? (
    <button
      className="tcp-btn active"
      onClick={() => setViewAll(false)}
    >
      ◼
    </button>
  ) : (
    <Tip text="view all items" side="bottom">
      <button
        className="tcp-btn"
        onClick={() => setViewAll(true)}
      >
        ◼
      </button>
    </Tip>
  )
)}

<EditorNote collectionKey={collectionNameFromState || "new"} />
         <Tip text="home" side="bottom">
  <button type="button" className="tcp-btn" onClick={() => navigate("/home-page")} disabled={uploading}>
    🏠
  </button>
</Tip>
        </div>
      </header>

      <main className="tcp-main">
        <div className="tcp-shell">
          {!files.length ? (
            <div className="tcp-empty">
              <p>no items in this collection yet.</p>
              <button className="tcp-btn" onClick={() => addMoreInputRef.current?.click()}>
                ➕
              </button>
            </div>
          ) : viewAll ? (
            <section className="tcp-grid" aria-label="view all items">
             <div className="tcp-grid-head">
                <div className="tcp-grid-head-left" />
                <div className="tcp-grid-head-right">
                  click an image to edit • drag to reorder • esc to exit
                </div>
              </div>
              <div
                className="tcp-grid-items"
                style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${viewAllTile}px, 1fr))` }}
              >
                {files.map((it, idx) => {
                  const thumb = it.previewUrl || it.originalPreviewUrl || it.originalUrl || it.fileUrl || "";
                  const title = (it.name || "").trim() || "untitled";

                  return (
                    <div
                      className="tcp-thumb"
                      key={it.id}
                      role="button"
                      tabIndex={0}
                      draggable
                      onDragStart={(e) => onThumbDragStart(e, idx)}
                      onDragOver={(e) => onThumbDragOver(e, idx)}
                      onDragLeave={onThumbDragLeave}
                      onDrop={(e) => onThumbDrop(e, idx)}
                      onDragEnd={onThumbDragEnd}
                      onClick={() => {
                        if (draggingIdRef.current) return;
                        setCurrentIndex(idx);
                        setViewAll(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setCurrentIndex(idx);
                          setViewAll(false);
                        }
                      }}
                      aria-label={`edit item ${idx + 1}`}
                    >
                      <img src={thumb} alt={it.altText || `item ${idx + 1}`} loading="lazy" />

                      <div className="tcp-thumb-meta">
                        <div className="tcp-thumb-title">{title}</div>
                        <div className="tcp-thumb-index">{idx + 1}</div>
                      </div>

                      <button
                        type="button"
                        className="tcp-thumb-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteItem(it.id);
                        }}
                        aria-label="delete item"
                        title="delete item"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : !currentItem ? (
            <div className="tcp-empty">
              <p>no item selected.</p>
            </div>
          ) : (
            <div className="tcp-split">
              <section className="tcp-left">{renderMainImage(currentItem)}</section>
              <section className="tcp-right" aria-label="metadata editor">
                <div className="tcp-metabody" ref={metaBodyRef}>
                  <div className="tcp-row tcp-row--visibility">
                    <div className="tcp-label">item visibility</div>
                    <div className="tcp-lockcell">
                      <Tip
  text={
    collectionPrivate
      ? "collection is private (item visibility locked)"
      : currentItem.itemPrivate
      ? "item is private"
      : "item is public"
  }
>
  <button
    type="button"
    className="tcp-iconbtn"
    disabled={collectionPrivate}
    onClick={(e) => {
      toggleItemPrivate(currentItem.id);
      e.currentTarget.blur();   // ← remove focus after click
    }}
  >
    {currentItem.itemPrivate ? "🔐" : "🔓"}
  </button>
</Tip>
                    </div>
                  </div>
                 
               

                  {CORE_FIELDS.map((f) => (
                    <FieldRow
                      key={f.key}
                      item={currentItem}
                      fieldSpec={f}
                      lockedDisabled={collectionPrivate || currentItem.itemPrivate}
                      knownArtists={knownArtists}
                      knownSources={knownSources}
                      knownTags={knownTags}
                      knownLocations={knownLocations}
                      updateField={updateField}
                      updateItem={updateItem}
                      toggleFieldPrivacy={toggleFieldPrivacy}
                      addAttachmentsToItem={addAttachmentsToItem}
                      removeAttachmentFromItem={removeAttachmentFromItem}
                    />
                  ))}

                  <div className="tcp-extras">
                    <button
                      type="button"
                      className="tcp-linkbtn"
                      onClick={() => toggleExtraFieldsPanel(currentItem.id)}
                    >
                      {currentItem.extraFieldsOpen ? "hide fields…" : "add fields…"}
                    </button>

                    {currentItem.extraFieldsOpen && (
                      <div className="tcp-extras-panel" role="group" aria-label="extra fields">
                        {EXTRA_FIELDS.map((f) => {
                          const enabled = normalizeEnabledExtras(currentItem).includes(f.key);
                          return (
                            <button
                              key={f.key}
                              type="button"
                              className={`tcp-chip ${enabled ? "is-on" : ""}`}
                              onClick={() => toggleExtraFieldEnabled(currentItem.id, f.key)}
                            >
                              {enabled ? "✓ " : "+ "}
                              {f.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {normalizeEnabledExtras(currentItem)
                    .filter((k) => EXTRA_FIELDS.some((f) => f.key === k))
                    .map((key) => {
                      const spec = EXTRA_FIELDS.find((f) => f.key === key);
                      if (!spec) return null;

                      return (
                        <FieldRow
                          key={spec.key}
                          item={currentItem}
                          fieldSpec={spec}
                          lockedDisabled={collectionPrivate || currentItem.itemPrivate}
                          knownArtists={knownArtists}
                          knownSources={knownSources}
                          knownTags={knownTags}
                          knownLocations={knownLocations}
                          updateField={updateField}
                          updateItem={updateItem}
                          toggleFieldPrivacy={toggleFieldPrivacy}
                          addAttachmentsToItem={addAttachmentsToItem}
                          removeAttachmentFromItem={removeAttachmentFromItem}
                        />
                      );
                    })}
                </div>

                {saveError && (
                  <div style={{ padding: "12px 14px", color: "rgba(255,120,120,.95)" }}>
                    {saveError}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </main>

      {/* FOOTER PAGER */}
    <footer className="vc-footer">

  <button className="vc-nav" onClick={() => {
    goPrev();
  }}>
    ←
  </button>

  <span className="vc-count">
    {currentIndex + 1} / {files.length}
  </span>


  <div className="vc-viewall-tip">
    press ← → keys to navigate
  </div>

  <button className="vc-nav" onClick={() => {
    goNext();
  }}>
    →
  </button>

</footer>

      {(compressing || uploading || progress.active) && (
        <div className="tcp-overlay">
          <div className="tcp-overlay-text">
            {uploading
              ? progress.active
                ? `saving… ${progress.percent}%`
                : "saving your collection…"
              : progress.active
              ? `${progress.label} ${progress.percent}%`
              : "updating…"}
          </div>
        </div>
      )}

     <ConfirmBulkLock
  open={!!lockPrompt}
  title={
    lockPrompt
      ? `${lockPrompt.nextValue ? "make private" : "make public"}: ${lockPrompt.label}`
      : ""
  }
  subtitle="apply this change across all items?"
  onYes={() => {
  if (!lockPrompt) return;

  // remember YES only for this field on this item (keeps your “don’t ask again for this field” behavior)
  setFiles((prev) =>
    prev.map((it) => {
      if (it.id !== lockPrompt.itemId) return it;
      const bulkLockAllFields = { ...(it.bulkLockAllFields || {}) };
      bulkLockAllFields[lockPrompt.fieldKey] = true;
      return { ...it, bulkLockAllFields };
    })
  );

  // ✅ apply to all existing items
  applyFieldPrivacyAll(lockPrompt.fieldKey, lockPrompt.nextValue);

  // ✅ set default for future new items
  setBulkFieldDefaults((prev) => ({
    ...prev,
    [lockPrompt.fieldKey]: lockPrompt.nextValue,
  }));

  setLockPrompt(null);
}}


  onNo={() => {
    if (!lockPrompt) return;

    // ✅ do NOT remember NO — next lock click will ask again
    applyFieldPrivacyOne(lockPrompt.itemId, lockPrompt.fieldKey, lockPrompt.nextValue);
    setLockPrompt(null);
  }}
/>

      {/* hidden inputs */}
      <input
        ref={addMoreInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.gif,.tif,.tiff"
        multiple
        style={{ display: "none" }}
        onChange={onAddMorePicked}
      />
      <input
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.gif,.tif,.tiff"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleReplaceFile}
      />
      <input
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.gif,.tif,.tiff"
        multiple
        style={{ display: "none" }}
        ref={additionalImageInputRef}
        onChange={handleAddAdditionalImages}
      />
    </div>
  );
}